/* global HTMLTextAreaElement, requestAnimationFrame */
// markdown-textarea.tsx — textarea + compact format toolbar (note body editor).
// Buttons insert/wrap Markdown at the caret so users don't need to memorise syntax.
// Pure controlled component: value/onChange owned by the form.
import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Bold, CheckSquare, Heading, Italic, Link2, List, Quote } from "lucide-react";
import { insertMention, mentionQuery } from "./note-mention";

type Props = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  rows?: number;
  /** P6-4: note titles for `[[` backlink autocomplete. */
  suggestions?: string[];
  /** Accessible name for the textarea. The wrapping <label> can't reach it
   *  (the toolbar buttons sit before the textarea), so set it explicitly. */
  ariaLabel?: string;
};

export function MarkdownTextarea({ value, onChange, className = "", rows = 5, suggestions = [], ariaLabel }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  // P6-4: active `[[` mention query (null = closed).
  const [mention, setMention] = useState<string | null>(null);

  const matches = mention !== null
    ? suggestions.filter((t) => t.toLowerCase().includes(mention.toLowerCase())).slice(0, 8)
    : [];

  /** Detect/refresh the `[[` mention after each edit. */
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart ?? v.length;
    setMention(suggestions.length ? mentionQuery(v.slice(0, caret)) : null);
  };

  /** Insert the chosen note title as [[Title]] and close the menu. */
  const pickMention = (title: string) => {
    const ta = ref.current;
    if (!ta) return;
    const res = insertMention(value, ta.selectionStart, title);
    setMention(null);
    if (!res) return;
    onChange(res.next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = res.caret;
    });
  };

  /** Wrap the current selection with `before`/`after` (e.g. ** ** for bold). */
  const wrap = (before: string, after: string = before) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = value.slice(0, start) + before + value.slice(start, end) + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = start + before.length;
      ta.selectionEnd = end + before.length;
    });
  };

  /** Prefix the line containing the caret with `prefix` (e.g. "## ", "- "). */
  const linePrefix = (prefix: string) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + prefix.length;
    });
  };

  const tools: { key: string; title: string; icon: typeof Bold; run: () => void }[] = [
    { key: "b", title: "Đậm", icon: Bold, run: () => wrap("**") },
    { key: "i", title: "Nghiêng", icon: Italic, run: () => wrap("*") },
    { key: "h", title: "Chèn tiêu đề", icon: Heading, run: () => linePrefix("## ") },
    { key: "ul", title: "Danh sách", icon: List, run: () => linePrefix("- ") },
    { key: "todo", title: "Việc cần làm", icon: CheckSquare, run: () => linePrefix("- [ ] ") },
    { key: "quote", title: "Trích dẫn", icon: Quote, run: () => linePrefix("> ") },
    { key: "link", title: "Liên kết", icon: Link2, run: () => wrap("[", "](url)") },
  ];

  return (
    <div className="relative rounded-xl border border-ink-200 bg-white focus-within:border-brand-500">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-ink-100 px-1.5 py-1">
        {tools.map((t) => (
          <button
            key={t.key}
            type="button"
            title={t.title}
            aria-label={t.title}
            onClick={t.run}
            className="grid h-7 w-7 place-items-center rounded-lg text-ink-500 transition hover:bg-brand-50 hover:text-brand-600"
          >
            <t.icon className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>
      <textarea
        ref={ref}
        rows={rows}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => { if (e.key === "Escape" && mention !== null) { e.stopPropagation(); setMention(null); } }}
        onBlur={() => setMention(null)}
        aria-label={ariaLabel}
        placeholder="Viết nội dung… (hỗ trợ Markdown: **đậm**, ## tiêu đề, - danh sách, [[liên kết]])"
        className={`w-full resize-y bg-transparent px-3 py-2 text-[14px] outline-none placeholder:text-ink-400 ${className}`}
      />

      {/* P6-4: backlink autocomplete dropdown — appears below the editor */}
      {mention !== null && matches.length > 0 && (
        <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-xl border border-ink-200 bg-white p-1 shadow-[0_12px_40px_rgba(31,29,43,0.16)]">
          {matches.map((t) => (
            <button
              key={t}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pickMention(t); }}
              className="block w-full truncate rounded-lg px-2.5 py-1.5 text-left text-[13px] font-medium text-ink-700 transition hover:bg-brand-50 hover:text-brand-600"
            >
              [[{t}]]
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
